import { useState } from "react";
import { useFavorites } from "@/hooks/useFavorites";

interface Props {
  id: string;
  title: string;
}

export default function ShareBar({ id, title }: Props) {
  const [qrOpen, setQrOpen] = useState(false);
  const [toast, setToast] = useState("");
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(id);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2000);
  };

  const shareUrl = () => (typeof window !== "undefined" ? window.location.href : "");

  const openExternal = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer,width=640,height=520");
  };

  const handleWeibo = () => {
    openExternal(
      `https://service.weibo.com/share/share.php?url=${encodeURIComponent(shareUrl())}&title=${encodeURIComponent(title)}`,
    );
  };

  const handleQQ = () => {
    openExternal(
      `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(shareUrl())}&title=${encodeURIComponent(title)}`,
    );
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl());
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  const handleFavorite = () => {
    toggleFavorite(id);
    showToast(favorited ? "已取消收藏" : "收藏成功，可在本机查看");
  };

  return (
    <div className="flex items-center flex-wrap gap-2 py-4 border-y border-background-200 my-6">
      <span className="text-sm text-foreground-500 mr-1">分享到：</span>
      <button
        type="button"
        onClick={handleWeibo}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-700 whitespace-nowrap transition-colors"
      >
        <i className="ri-weibo-fill text-accent-500" />
        微博
      </button>
      <button
        type="button"
        onClick={() => setQrOpen(true)}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-primary-100 hover:text-primary-700 whitespace-nowrap transition-colors"
      >
        <i className="ri-wechat-fill text-primary-500" />
        微信
      </button>
      <button
        type="button"
        onClick={handleQQ}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-secondary-100 hover:text-secondary-700 whitespace-nowrap transition-colors"
      >
        <i className="ri-qq-fill text-secondary-500" />
        QQ
      </button>
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-background-200 whitespace-nowrap transition-colors"
      >
        <i className="ri-link" />
        复制链接
      </button>
      <button
        type="button"
        onClick={handleFavorite}
        className={`ml-auto inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
          favorited
            ? "bg-accent-500 text-background-50"
            : "bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-700"
        }`}
      >
        <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} />
        {favorited ? "已收藏" : "收藏"}
      </button>

      {qrOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" onClick={() => setQrOpen(false)}>
          <div
            className="relative bg-background-50 rounded-lg p-6 w-[300px] text-center animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setQrOpen(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100"
              aria-label="关闭"
            >
              <i className="ri-close-line text-xl" />
            </button>
            <h3 className="text-base font-bold text-foreground-950">微信扫码分享</h3>
            <div className="mx-auto mt-4 w-44 h-44 rounded-lg bg-background-100 border border-background-200 flex flex-col items-center justify-center gap-2">
              <i className="ri-qr-code-line text-6xl text-foreground-800" />
              <span className="text-xs text-foreground-400">扫码在微信中打开</span>
            </div>
            <p className="mt-4 text-xs text-foreground-500">打开微信扫一扫，即可分享给好友或朋友圈</p>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-10 z-[70] px-5 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}